import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { saveIntendedRoute } from '../utils/routeUtils';

/**
 * Start Trial Page Component
 * For existing users to start their subscription trial
 * Redirects to login if not authenticated (not signup)
 */
const StartTrial: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token');
    if (!token && !user) {
      // Redirect to login (not signup) for existing users
      saveIntendedRoute('/start-trial');
      navigate('/auth?redirect=/start-trial&mode=login');
      return;
    }
    setCheckingAuth(false);
  }, [user, navigate]);

  const handleStartTrial = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Double-check authentication
      const token = localStorage.getItem('auth_token');
      if (!token) {
        saveIntendedRoute('/start-trial');
        navigate('/auth?redirect=/start-trial&mode=login');
        return;
      }

      // Create checkout session
      const response = await api.post('/billing/create-checkout-session', {
        lookup_key: 'thetraders_pro_monthly_v1',
      });

      if (response.data.success && response.data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else {
        setError('Failed to create checkout session. Please try again.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Checkout session creation error:', err);
      
      // Handle 401 - user not authenticated
      if (err?.response?.status === 401) {
        saveIntendedRoute('/start-trial');
        navigate('/auth?redirect=/start-trial&mode=login');
        return;
      }

      // Handle 403 - admin users
      if (err?.response?.status === 403) {
        setError('Admin users do not require a subscription. You already have full access.');
        setLoading(false);
        return;
      }
      
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to start checkout. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Hero Section */}
      <section className="text-center py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Start Your 7-Day Free Trial
          </h1>
          <p className="text-lg text-gray-400 mb-8">
            Welcome back! Upgrade to TheTraders Pro and unlock the full power of our platform.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 relative">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-cyan-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                TheTraders Pro
              </span>
            </div>

            {/* Pricing Header */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">TheTraders Pro</h3>
              
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-5xl font-bold text-white">$39.99</span>
                <span className="text-xl text-gray-400 ml-2">/ month</span>
              </div>
              
              <p className="text-sm text-gray-400">
                7-day free trial · Cancel anytime
              </p>
            </div>

            {/* Features List */}
            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">AI-generated trading policy suggestions</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Automated 3-trades-per-week workflow</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Portfolio-aware risk & position limits</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Personal dashboard & trading analytics</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Priority support</span>
              </li>
            </ul>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Stripe Checkout Form */}
            <form onSubmit={handleStartTrial}>
              <input
                type="hidden"
                name="lookup_key"
                value="thetraders_pro_monthly_v1"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                {loading ? 'Processing...' : 'Start 7-Day Free Trial'}
              </button>
            </form>

            {/* Note */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Card required. You won't be charged until the trial ends.
            </p>

            {/* Existing User Note */}
            {user && (
              <div className="mt-6 p-3 bg-cyan-900/20 border border-cyan-700/50 rounded-lg">
                <p className="text-cyan-300 text-sm text-center">
                  Logged in as: {user.email}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StartTrial;

